import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import featureFlagService from '../services/featureFlagService';
import NewLoanModal from '../components/broker/NewLoanModal';

const BrokerLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNewLoanModal, setShowNewLoanModal] = useState(false);
  const [featureFlags, setFeatureFlags] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const isBrokerAdmin = currentUser?.role === 'BrokerAdmin';

  // Load feature flags on mount and listen for changes
  useEffect(() => {
    const loadFlags = () => {
      setFeatureFlags(featureFlagService.getFeatureFlags());
    };
    
    loadFlags();
    
    // Listen for storage changes (when Lender Admin updates flags)
    const handleStorageChange = (e) => {
      if (e.key === 'uff_broker_feature_flags') {
        loadFlags();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for same-window updates
    const interval = setInterval(loadFlags, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  
  const isFeatureEnabled = (featureKey) => {
    return featureFlags[featureKey] === true;
  };

  return (
    <div className="page">
      <aside className={`navbar navbar-vertical navbar-expand-lg ${sidebarOpen ? 'show' : ''}`}>
        <div className="container-fluid">
          <button 
            className="navbar-toggler" 
            type="button"
            onClick={() => setSidebarOpen(false)}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <h1 className="navbar-brand navbar-brand-autodark d-none-navbar-horizontal pe-0 pe-md-3">
            <Link to={isBrokerAdmin ? "/broker/dashboard" : "/broker/pipeline"}>
              <img src="/assets/UFF_Logo_Main_2026.png" alt="United Fidelity Funding Corp" style={{ height: '40px', width: 'auto' }} />
            </Link>
          </h1>
          
          <div className="navbar-nav">
            {/* Scenario Pricer - Available to all broker users */}
            {isFeatureEnabled('scenarioPricer') && (
              <div className="nav-item">
                <Link 
                  to="/broker/scenario-pricer" 
                  className={`nav-link ${isActive('/broker/scenario-pricer') ? 'active' : ''}`}
                >
                  <span className="nav-link-icon d-md-none d-lg-inline-block">
                    <i className="ti ti-calculator"></i>
                  </span>
                  <span className="nav-link-title">Scenario Pricer</span>
                </Link>
              </div>
            )}

            {isBrokerAdmin ? (
              <>
                {isFeatureEnabled('dashboard') && (
                  <div className="nav-item">
                    <Link 
                      to="/broker/dashboard" 
                      className={`nav-link ${isActive('/broker/dashboard') ? 'active' : ''}`}
                    >
                      <span className="nav-link-icon d-md-none d-lg-inline-block">
                        <i className="ti ti-layout-dashboard"></i>
                      </span>
                      <span className="nav-link-title">Dashboard</span>
                    </Link>
                  </div>
                )}
                {isFeatureEnabled('users') && (
                  <div className="nav-item">
                    <Link 
                      to="/broker/users" 
                      className={`nav-link ${isActive('/broker/users') ? 'active' : ''}`}
                    >
                      <span className="nav-link-icon d-md-none d-lg-inline-block">
                        <i className="ti ti-users"></i>
                      </span>
                      <span className="nav-link-title">Users</span>
                    </Link>
                  </div>
                )}
                {isFeatureEnabled('offices') && (
                  <div className="nav-item">
                    <Link 
                      to="/broker/offices" 
                      className={`nav-link ${isActive('/broker/offices') ? 'active' : ''}`}
                    >
                      <span className="nav-link-icon d-md-none d-lg-inline-block">
                        <i className="ti ti-building"></i>
                      </span>
                      <span className="nav-link-title">Offices</span>
                    </Link>
                  </div>
                )}
                {isFeatureEnabled('compensation') && (
                  <div className="nav-item">
                    <Link 
                      to="/broker/compensation" 
                      className={`nav-link ${isActive('/broker/compensation') ? 'active' : ''}`}
                    >
                      <span className="nav-link-icon d-md-none d-lg-inline-block">
                        <i className="ti ti-currency-dollar"></i>
                      </span>
                      <span className="nav-link-title">Compensation</span>
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <>
                {isFeatureEnabled('pipeline') && (
                  <div className="nav-item">
                    <Link 
                      to="/broker/pipeline" 
                      className={`nav-link ${isActive('/broker/pipeline') ? 'active' : ''}`}
                    >
                      <span className="nav-link-icon d-md-none d-lg-inline-block">
                        <i className="ti ti-list"></i>
                      </span>
                      <span className="nav-link-title">My Pipeline</span>
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </aside>

      <header className="navbar navbar-expand-md d-print-none">
        <div className="container-xl">
          <button 
            className="navbar-toggler" 
            type="button"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <h1 className="navbar-brand navbar-brand-autodark d-none-navbar-horizontal pe-0 pe-md-3">
            <Link to={isBrokerAdmin ? "/broker/dashboard" : "/broker/pipeline"} style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                <span style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>PRO Portal</span>
                <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>(powered by UFF)</span>
              </div>
            </Link>
          </h1>
          
          <div className="navbar-nav flex-row order-md-last">
            {isFeatureEnabled('newLoanButton') && (
              <div className="nav-item me-3">
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowNewLoanModal(true)}
                >
                  <i className="ti ti-plus me-1"></i>
                  New Loan
                </button>
              </div>
            )}
            <div className="nav-item dropdown">
              <a href="#" className="nav-link d-flex lh-1 text-reset p-0" data-bs-toggle="dropdown">
                <span className="avatar avatar-sm" style={{ backgroundImage: `url(${currentUser?.avatar})` }}></span>
                <div className="d-none d-xl-block ps-2">
                  <div>{currentUser?.name}</div>
                  <div className="mt-1 small text-secondary">
                    {isBrokerAdmin ? 'Broker Admin' : 'Loan Officer'}
                  </div>
                </div>
              </a>
              <div className="dropdown-menu dropdown-menu-end dropdown-menu-arrow">
                <button onClick={handleLogout} className="dropdown-item">
                  <i className="ti ti-logout me-2"></i>
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <div className="page-wrapper">
        <div className="page-body">
          <div className="container-xl">
            <Outlet />
          </div>
        </div>
      </div>

      {/* New Loan Modal */}
      <NewLoanModal
        show={showNewLoanModal}
        onClose={() => setShowNewLoanModal(false)}
        onLoanCreated={(loan) => {
          setShowNewLoanModal(false);
          // Navigate to the new loan detail page
          navigate(`/broker/loans/${loan.id}`);
        }}
      />
    </div>
  );
};

export default BrokerLayout;
